import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";
import { RiBillLine } from "react-icons/ri";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { toast } from "react-toastify";
import { renderCurrencyIconColored } from "../../../utils/CurrencyUtils";
import {
  formatIntegerAmount,
  parseIntegerAmount,
} from "../../../utils/CommonFunction";
import CInput from "../../../components/CInput";

const requiredLabel = (text) => (
  <>
    {text}
    <span style={{ color: "#DC2626", marginLeft: 2 }}>*</span>
  </>
);

const inputColorSx = {
  "& .MuiInputBase-input": {
    color: "#0d0d0e !important",
    WebkitTextFillColor: "#0d0d0e !important",
    padding: "3px 5px !important",
    fontSize: 13,
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#0d0d0e !important",
    WebkitTextFillColor: "#0d0d0e !important",
  },
  "& fieldset": {
    padding: "2px 8px !important",
  },
};

const compactInputSx = {
  "& .MuiInputBase-input": {
    color: "#0d0d0e !important",
    WebkitTextFillColor: "#0d0d0e !important",
    padding: "5px 4px !important",
    fontSize: 13,
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#0d0d0e !important",
    WebkitTextFillColor: "#0d0d0e !important",
  },
};

const isMandatoryName = (name) => {
  const n = (name || "").trim().toLowerCase();
  return n === "initial deposit" || n === "handover";
};

const isMandatoryMilestone = (milestone) => {
  if (!milestone) return false;
  if (milestone.isInitialDeposit) return true;
  if (milestone.isMandatory) return true;
  const type = (milestone.type || "").toUpperCase();
  if (type === "PROJECT_INITIAL_DEPOSIT" || type === "HANDOVER") return true;
  return isMandatoryName(milestone.description || milestone.name);
};

const isInitialDepositMilestone = (milestone) => {
  if (!milestone) return false;
  if (milestone.isInitialDeposit) return true;
  const type = (milestone.type || "").toUpperCase();
  if (type === "PROJECT_INITIAL_DEPOSIT") return true;
  return (
    (milestone.description || milestone.name || "").trim().toLowerCase() ===
    "initial deposit"
  );
};

/* ---------- Sub-activity row (display + edit modes) ---------- */
export const SubActivityRow = ({
  index,
  parentIndex,
  value,
  onChange,
  onDelete,
  disabled,
  isEditing,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  autoFocus = false,
  deleted = false,
}) => {
  if (deleted) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          ml: 7,
          py: 0.5,
          opacity: 0.6,
        }}
      >
        <Typography
          sx={{ fontSize: 12, color: "#9CA3AF", width: 28, flexShrink: 0 }}
        >
          {parentIndex + 1}.{index + 1}
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            color: "#6B7280",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: "line-through",
          }}
        >
          {value || "—"}
        </Typography>
      </Box>
    );
  }

  if (!isEditing) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          ml: 7,
          py: 0.5,
          "&:hover .sub-row-actions": { opacity: 1 },
        }}
      >
        <Typography
          sx={{ fontSize: 12, color: "#9CA3AF", width: 28, flexShrink: 0 }}
        >
          {parentIndex + 1}.{index + 1}
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            color: value ? "#0D0D0E" : "#9CA3AF",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value || "—"}
        </Typography>
        {!disabled && (
          <Box
            className="sub-row-actions"
            sx={{
              display: "flex",
              gap: 0.25,
              opacity: { xs: 1, md: 0 },
              transition: "opacity 0.15s",
            }}
          >
            <IconButton
              size="small"
              onClick={onStartEdit}
              title="Edit"
              sx={{ color: "#6B7280", p: 0.5 }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={onDelete}
              title="Delete"
              sx={{ color: "#9CA3AF", p: 0.5 }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        ml: 7,
        py: 0.5,
      }}
    >
      <Typography
        sx={{ fontSize: 12, color: "#9CA3AF", width: 28, flexShrink: 0 }}
      >
        {parentIndex + 1}.{index + 1}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#4D6BF6",
            mr: 0.5,
          }}
        >
          <AddIcon sx={{ fontSize: 14 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CInput
            placeholder="Sub-activity name…"
            value={value || ""}
            autoFocus={autoFocus}
            onChange={(e) => onChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onSaveEdit?.();
              } else if (e.key === "Escape") {
                e.preventDefault();
                onCancelEdit?.();
              }
            }}
            sx={compactInputSx}
          />
        </Box>
      </Box>
      <IconButton
        size="small"
        onClick={onSaveEdit}
        title="Save"
        sx={{ color: "#16A34A" }}
      >
        <CheckIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={onCancelEdit}
        title="Cancel"
        sx={{ color: "#9CA3AF" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

/* ---------- Deliverable row (display + edit modes) ---------- */
export const DeliverableRow = ({
  deliverable,
  index,
  onUpdateName,
  onDelete,
  onAddSub,
  onUpdateSubName,
  onDeleteSub,
  disabled,
  autoFocus = false,
  deleted = false,
}) => {
  const subs = deliverable.sub_activities || [];
  const [expanded, setExpanded] = useState(subs.length > 0);
  const [editing, setEditing] = useState(autoFocus);
  const [editingWasNew, setEditingWasNew] = useState(autoFocus);
  const [editingSubIdx, setEditingSubIdx] = useState(-1);
  const [editingSubWasNew, setEditingSubWasNew] = useState(false);

  useEffect(() => {
    if (autoFocus) {
      setEditing(true);
      setEditingWasNew(true);
    }
  }, [autoFocus]);

  useEffect(() => {
    if (subs.length > 0) {
      setExpanded(true);
    }
  }, [subs.length]);

  // Soft-deleted deliverable — inert strikethrough row. Returned
  // after all hooks above so the hook order stays stable.
  if (deleted) {
    return (
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: "8px",
          mb: 0.75,
          opacity: 0.6,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            p: 1,
          }}
        >
          <Box sx={{ width: 32, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#6B7280",
              flexShrink: 0,
            }}
          >
            {index + 1}.
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#6B7280",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ml: 0.5,
              textDecoration: "line-through",
            }}
          >
            {deliverable.deliverable_description || "Untitled"}
          </Typography>
        </Box>
      </Box>
    );
  }

  const handleAddSub = () => {
    setExpanded(true);
    onAddSub?.();
    setEditingSubIdx(subs.length);
    setEditingSubWasNew(true);
  };

  const handleStartEditSub = (subIdx) => {
    setEditingSubIdx(subIdx);
    setEditingSubWasNew(false);
  };

  const handleSaveSubEdit = () => {
    const v = (subs[editingSubIdx]?.sub_activity_description || "").trim();
    // Empty save on a never-committed sub abandons the initial add
    // (hard-remove); empty save on an existing sub still flows as a
    // normal delete (soft-delete in the change-request flow).
    if (!v) onDeleteSub?.(editingSubIdx, editingSubWasNew);
    setEditingSubIdx(-1);
    setEditingSubWasNew(false);
  };

  const handleCancelSubEdit = () => {
    if (editingSubWasNew) onDeleteSub?.(editingSubIdx, true);
    setEditingSubIdx(-1);
    setEditingSubWasNew(false);
  };

  const handleStartEdit = () => {
    setEditing(true);
    setEditingWasNew(false);
  };

  const handleSaveEdit = () => {
    const v = (deliverable.deliverable_description || "").trim();
    if (!v) {
      // Empty save on a never-committed deliverable abandons the
      // initial add (hard-remove); empty save on an existing one
      // still flows as a normal delete (soft-delete in CR flow).
      onDelete?.(editingWasNew);
      return;
    }
    setEditing(false);
    setEditingWasNew(false);
  };

  const handleCancelEdit = () => {
    if (editingWasNew) {
      onDelete?.(true);
      return;
    }
    setEditing(false);
    setEditingWasNew(false);
  };

  const subCount = subs.filter((s) => s.status !== "DELETED").length;
  const subPillLabel = `${subCount} ${subCount === 1 ? "activity" : "activities"}`;

  if (editing) {
    return (
      <Box
        sx={{
          mb: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          position: "relative",
        }}
      >
        <Box
          sx={{
            color: "#4D6BF6",
            display: "flex",
            alignItems: "center",
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CInput
            placeholder="Deliverable name…"
            value={deliverable.deliverable_description || ""}
            autoFocus={autoFocus}
            onChange={(e) => onUpdateName?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSaveEdit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                handleCancelEdit();
              }
            }}
            disabled={disabled}
            sx={{
              ...compactInputSx,
              "& .MuiInputBase-input": {
                ...(compactInputSx["& .MuiInputBase-input"] || {}),
                paddingLeft: "26px !important",
              },
            }}
          />
        </Box>
        <IconButton
          size="small"
          onClick={handleSaveEdit}
          title="Save"
          sx={{ color: "#16A34A" }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleCancelEdit}
          title="Cancel"
          sx={{ color: "#9CA3AF" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        mb: 0.75,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          p: 1,
        }}
      >
        <IconButton
          size="small"
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? "Hide sub-activities" : "Show sub-activities"}
        >
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "#6B7280",
            flexShrink: 0,
          }}
        >
          {index + 1}.
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: deliverable.deliverable_description ? "#0D0D0E" : "#9CA3AF",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            ml: 0.5,
          }}
        >
          {deliverable.deliverable_description || "Untitled"}
        </Typography>
        {subCount > 0 && (
          <Box
            sx={{
              padding: "2px 10px",
              borderRadius: "999px",
              backgroundColor: "#F3F4F6",
              color: "#6B7280",
              fontSize: 12,
              fontWeight: 500,
              fontFamily: "Inter Tight",
              flexShrink: 0,
            }}
          >
            {subPillLabel}
          </Box>
        )}
        {!disabled && (
          <>
            <IconButton
              size="small"
              onClick={handleAddSub}
              title="Add sub-activity"
              sx={{ color: "#4D6BF6" }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleStartEdit}
              title="Edit"
              sx={{ color: "#6B7280" }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete?.()}
              title="Delete"
              sx={{ color: "#9CA3AF" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ borderTop: "1px solid #F3F4F6", py: 0.75, pr: 1 }}>
          {subs.map((sub, si) => (
            <SubActivityRow
              key={sub.id || si}
              index={si}
              parentIndex={index}
              value={sub.sub_activity_description}
              onChange={(text) =>
                onUpdateSubName?.(si, text)
              }
              onDelete={() => onDeleteSub?.(si)}
              disabled={disabled}
              isEditing={editingSubIdx === si}
              onStartEdit={() => handleStartEditSub(si)}
              onSaveEdit={handleSaveSubEdit}
              onCancelEdit={handleCancelSubEdit}
              autoFocus={editingSubIdx === si && editingSubWasNew}
              deleted={sub.status === "DELETED"}
            />
          ))}
          {!disabled && editingSubIdx === -1 && (
            <Box sx={{ ml: 7, mt: 0.5 }}>
              <button
                type="button"
                onClick={handleAddSub}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#6B7280",
                  padding: "4px 0",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  fontFamily: "Inter Tight",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <AddIcon sx={{ fontSize: 16, color: "#4D6BF6" }} />
                Add sub-activity
              </button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

/* ---------- Main component ---------- */
const formatCurrencyInt = (n) => formatIntegerAmount(n) || "0";

const BillingMilestones = ({
  billingMilestones,
  hasMilestoneChanges,
  isMilestoneLoading,
  addMilestone,
  deleteMilestone,
  updateMilestone,
  saveMilestoneChanges,
  reorderMilestones,
  addDeliverable,
  updateDeliverable,
  deleteDeliverable,
  addSubActivity,
  updateSubActivity,
  deleteSubActivity,
  totalPercentage,
  proposalValue,
  isDisabled = false,
  hideSaveButton = false,
}) => {
  const [expandedDeliverablesIdx, setExpandedDeliverablesIdx] = useState(null);
  const [autoFocusKey, setAutoFocusKey] = useState({ ms: -1, del: -1 });
  const milestoneRefs = React.useRef([]);
  const prevCountRef = React.useRef((billingMilestones || []).length);

  React.useEffect(() => {
    const count = (billingMilestones || []).length;
    if (count > prevCountRef.current) {
      const lastIdx = count - 1;
      const node = milestoneRefs.current[lastIdx];
      if (node && typeof node.scrollIntoView === "function") {
        node.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
    prevCountRef.current = count;
  }, [billingMilestones?.length]);

  const computedTotal =
    typeof totalPercentage === "number"
      ? totalPercentage
      : (billingMilestones || []).reduce(
          (acc, m) => acc + (Number(m.percentage) || 0),
          0
        );
  const isComplete = Math.abs(computedTotal - 100) < 0.01;

  const totalAmount = (billingMilestones || []).reduce(
    (acc, m) => acc + (Number(m.amount) || 0),
    0
  );
  const proposalValueNum = Number(proposalValue) || 0;
  const amountMatchesProposal =
    proposalValueNum > 0 && Math.abs(totalAmount - proposalValueNum) < 0.01;
  const amountExceedsProposal =
    proposalValueNum > 0 && totalAmount - proposalValueNum > 0.01;
  const showAmountWarningRow =
    proposalValueNum > 0 && !amountMatchesProposal;

  const hasIncompleteDeliverable = (milestone) =>
    (milestone?.deliverables || []).some(
      (d) => !(d?.deliverable_description || "").trim()
    );

  const handleAddDeliverable = (msIndex, currentLen) => {
    const milestone = (billingMilestones || [])[msIndex];
    if (hasIncompleteDeliverable(milestone)) {
      toast.error("Please name the existing deliverable before adding a new one");
      return;
    }
    setExpandedDeliverablesIdx(msIndex);
    addDeliverable?.(msIndex);
    setAutoFocusKey({ ms: msIndex, del: currentLen });
  };

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "none",
        margin: "1vh 0",
        border: "1px solid #E5E7EB",
        overflow: "initial",
      }}
    >
      <CardContent sx={{ padding: "14px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
            mb: 1.25,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                letterSpacing: 0,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "16px",
              }}
            >
              <RiBillLine color="#4D6BF6" size={20} />
              Billing Milestones
            </Typography>

            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                padding: "4px 10px",
                borderRadius: "999px",
                border: `1px solid ${isComplete ? "#16A34A" : "#DC2626"}`,
                backgroundColor: isComplete ? "#ECFDF5" : "#FEF2F2",
                color: isComplete ? "#16A34A" : "#DC2626",
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "Inter Tight",
              }}
            >
              Total: {computedTotal.toFixed(2)}%
            </Box>
          </Box>

          {!isDisabled && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <button
                onClick={addMilestone}
                style={{
                  backgroundColor: "#4D6BF6",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: 600,
                  boxShadow: "none",
                  padding: "6px 14px",
                  border: "1px solid transparent",
                  color: "#fff",
                  cursor: "pointer",
                  fontFamily: "Inter Tight",
                }}
              >
                Add Milestone
              </button>
              {!hideSaveButton && (
                <button
                  onClick={saveMilestoneChanges}
                  disabled={!hasMilestoneChanges || isMilestoneLoading}
                  style={{
                    backgroundColor: hasMilestoneChanges ? "#10B981" : "#9CA3AF",
                    borderRadius: "10px",
                    fontSize: "13px",
                    fontWeight: 600,
                    boxShadow: "none",
                    padding: "6px 14px",
                    border: "1px solid transparent",
                    color: "#fff",
                    cursor: hasMilestoneChanges ? "pointer" : "not-allowed",
                    fontFamily: "Inter Tight",
                  }}
                >
                  {isMilestoneLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    "Save Changes"
                  )}
                </button>
              )}
            </Box>
          )}
        </Box>

        {showAmountWarningRow && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              padding: "8px 12px",
              borderRadius: "8px",
              border: `1px solid ${
                amountExceedsProposal ? "#DC2626" : "#F59E0B"
              }`,
              backgroundColor: amountExceedsProposal ? "#FEF2F2" : "#FFFBEB",
              color: amountExceedsProposal ? "#991B1B" : "#92400E",
              fontFamily: "Inter Tight",
              fontSize: 12.5,
              mb: 1.25,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontWeight: 500,
              }}
            >
              {amountExceedsProposal
                ? "Milestone amounts exceed the proposal value"
                : "Milestone amounts should sum up to the proposal value"}
            </Box>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                fontWeight: 600,
              }}
            >
              {/* <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                {renderCurrencyIconColored("black-xs-small")}
                {formatCurrencyInt(totalAmount)}
              </Box>
              <Box component="span" sx={{ color: "inherit", opacity: 0.6 }}>
                /
              </Box> */}
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                {renderCurrencyIconColored("black-xs-small")}
                {formatCurrencyInt(proposalValueNum)}
              </Box>
            </Box>
          </Box>
        )}

        {(!billingMilestones || billingMilestones.length === 0) && (
          <Typography sx={{ fontSize: 13, color: "#9CA3AF", py: 2 }}>
            No milestones yet. Click "Add Milestone" to create one.
          </Typography>
        )}

        <DragDropContext
          onDragEnd={(result) => {
            if (!result.destination) return;
            const from = result.source.index;
            const to = result.destination.index;
            if (from === to) return;
            const initialDepositIdx = (billingMilestones || []).findIndex(
              isInitialDepositMilestone
            );
            if (initialDepositIdx !== -1) {
              if (from === initialDepositIdx) return;
              if (to <= initialDepositIdx) return;
            }
            reorderMilestones?.(from, to);
          }}
        >
          <Droppable droppableId="milestones-list">
            {(droppableProvided) => (
              <Box
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {billingMilestones?.map((milestone, index) => {
                  const isExisting =
                    milestone.id &&
                    typeof milestone.id === "string" &&
                    !milestone.id.startsWith("m_");
                  const isEditable = !isDisabled && !isExisting;
                  const isMandatory = isMandatoryMilestone(milestone);
                  const isInitialDeposit = isInitialDepositMilestone(milestone);
                  const allowDeliverables = !isInitialDeposit;
                  const dueDateRequired = isInitialDeposit;
                  const deliverables = milestone.deliverables || [];
                  const isExpanded =
                    allowDeliverables && expandedDeliverablesIdx === index;
                  const draggableId = String(milestone.id || `ms_${index}`);
                  const dragDisabled = isDisabled || isInitialDeposit;

                  return (
                    <Draggable
                      key={draggableId}
                      draggableId={draggableId}
                      index={index}
                      isDragDisabled={dragDisabled}
                    >
                      {(draggableProvided, snapshot) => (
                        <Box
                          ref={(el) => {
                            draggableProvided.innerRef(el);
                            milestoneRefs.current[index] = el;
                          }}
                          {...draggableProvided.draggableProps}
                          sx={{
                            borderRadius: "4px",
                            marginBottom: "8px",
                            backgroundColor: snapshot.isDragging
                              ? "#EEF2FF"
                              : isExpanded
                              ? "#F9FAFB"
                              : "#fff",
                            border: snapshot.isDragging
                              ? "1px solid #4D6BF6"
                              : isExpanded
                              ? "1px solid #E5E7EB"
                              : "1px solid transparent",
                            borderBottom: {
                              xs: snapshot.isDragging
                                ? "1px solid #4D6BF6"
                                : "1px solid #E5E7EB",
                              md: snapshot.isDragging
                                ? "1px solid #4D6BF6"
                                : isExpanded
                                ? "1px solid #E5E7EB"
                                : "1px solid transparent",
                            },
                            transition:
                              "background-color 0.2s ease, border-color 0.2s ease",
                            ...draggableProvided.draggableProps.style,
                          }}
                          style={draggableProvided.draggableProps.style}
                        >
                          <Box
                            sx={{
                              padding: { xs: "8px 10px", md: "8px 10px" },
                              display: "flex",
                              alignItems: { xs: "stretch", md: "center" },
                              gap: { xs: 0.5, md: "10px" },
                              flexWrap: { xs: "wrap", md: "nowrap" },
                            }}
                          >
                            {/* Drag handle — left on md, bottom-left on xs */}
                            {!dragDisabled ? (
                              <Box
                                {...draggableProvided.dragHandleProps}
                                sx={{
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#9CA3AF",
                                  cursor: "grab",
                                  "&:hover": { color: "#4D6BF6" },
                                  "&:active": { cursor: "grabbing" },
                                  mt: { xs: 0, md: 0.5 },
                                  order: { xs: 2, md: 0 },
                                }}
                                title="Drag to reorder"
                              >
                                <DragIndicatorIcon sx={{ fontSize: 20 }} />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: 20,
                                  flexShrink: 0,
                                  order: { xs: 2, md: 0 },
                                  display: { xs: "none", md: "block" },
                                }}
                              />
                            )}
                            {/* Chevron — far right next to delete on md, bottom-right on xs/sm */}
                            {allowDeliverables ? (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setExpandedDeliverablesIdx(
                                    isExpanded ? null : index
                                  )
                                }
                                sx={{
                                  flexShrink: 0,
                                  mt: { xs: 0, md: 0.25 },
                                  order: { xs: 3, md: 4 },
                                  ml: { xs: "auto", md: 0 },
                                }}
                                title={
                                  isExpanded
                                    ? "Hide deliverables"
                                    : "View deliverables"
                                }
                              >
                                {isExpanded ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            ) : (
                              <Box
                                aria-hidden
                                sx={{
                                  flexShrink: 0,
                                  width: 34,
                                  height: 34,
                                  mt: { md: 0.25 },
                                  order: { md: 4 },
                                  display: { xs: "none", md: "block" },
                                }}
                              />
                            )}
                            {/* Inputs grid:
                                xs (mobile) → 1 col: Phase, Amount, Percentage, Due Date stacked
                                sm (tablet) → 2 cols: 2x2
                                md+ (desktop) → flex row of 4 inputs */}
                            <Box
                              sx={{
                                flex: { md: 1 },
                                minWidth: 0,
                                width: { xs: "100%", md: "auto" },
                                display: { xs: "grid", md: "flex" },
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  sm: "1fr 1fr",
                                },
                                alignItems: "flex-start",
                                gap: { xs: 0.75, md: "10px" },
                                order: { xs: 1, md: 2 },
                              }}
                            >
                              <Box
                                sx={{
                                  flex: { md: 2 },
                                  minWidth: { md: 140 },
                                  width: { xs: "100%", md: "auto" },
                                }}
                              >
                                <CInput
                                  label={requiredLabel("Milestone Phase")}
                                  placeholder="Milestone Phase"
                                  value={
                                    milestone.description ||
                                    milestone.name ||
                                    ""
                                  }
                                  onChange={(e) =>
                                    updateMilestone?.(
                                      index,
                                      "description",
                                      e.target.value
                                    )
                                  }
                                  required
                                  disabled={!isEditable || isMandatory}
                                  sx={inputColorSx}
                                />
                              </Box>
                               <Box
                                sx={{
                                  flex: { md: 1 },
                                  minWidth: { md: 90 },
                                  width: { xs: "100%", md: "auto" },
                                }}
                              >
                                <CInput
                                  label={requiredLabel("Percentage (%)")}
                                  placeholder="%"
                                  type="number"
                                  inputProps={{ min: 0, step: 1 }}
                                  value={milestone.percentage || ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (
                                      v === "" ||
                                      /^\d+(\.\d{0,2})?$/.test(v)
                                    ) {
                                      updateMilestone?.(
                                        index,
                                        "percentage",
                                        v
                                      );
                                    }
                                  }}
                                  onWheel={(e) => e.target.blur()}
                                  required
                                  disabled={!isEditable}
                                  sx={inputColorSx}
                                />
                              </Box>
                              <Box
                                sx={{
                                  flex: { md: 1 },
                                  minWidth: { md: 100 },
                                  width: { xs: "100%", md: "auto" },
                                }}
                              >
                                <CInput
                                  label={requiredLabel("Amount")}
                                  placeholder="0"
                                  type="text"
                                  inputMode="numeric"
                                  value={formatIntegerAmount(milestone.amount)}
                                  required
                                  disabled
                                  InputProps={{ readOnly: true }}
                                  sx={inputColorSx}
                                />
                              </Box>
                             
                              <Box
                                sx={{
                                  flex: { md: 1.2 },
                                  minWidth: { md: 130 },
                                  width: { xs: "100%", md: "auto" },
                                }}
                              >
                                <CInput
                                  label={
                                    dueDateRequired
                                      ? requiredLabel("Due Date")
                                      : "Due Date"
                                  }
                                  type="date"
                                  InputLabelProps={{ shrink: true }}
                                  InputProps={{
                                    min: new Date().toISOString().split("T")[0],
                                  }}
                                  value={milestone.due_date || ""}
                                  onChange={(e) =>
                                    updateMilestone?.(
                                      index,
                                      "due_date",
                                      e.target.value
                                    )
                                  }
                                  disabled={!isEditable}
                                  sx={inputColorSx}
                                />
                              </Box>
                            </Box>

                            {/* Delete — far right on md, end of bottom row on xs */}
                            {!isDisabled && !isMandatory ? (
                              <Box
                                sx={{
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  mt: { xs: 0, md: 0.5 },
                                  order: { xs: 4, md: 3 },
                                }}
                              >
                                <IconButton
                                  onClick={() => deleteMilestone?.(index)}
                                  sx={{
                                    flexShrink: 0,
                                    color: "#DC2626",
                                    backgroundColor: "rgba(220, 38, 38, 0.08)",
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(220, 38, 38, 0.15)",
                                    },
                                  }}
                                  size="small"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box
                                aria-hidden
                                sx={{
                                  flexShrink: 0,
                                  width: 34,
                                  height: 34,
                                  mt: { md: 0.5 },
                                  order: { md: 3 },
                                  display: { xs: "none", md: "block" },
                                }}
                              />
                            )}
                          </Box>

              {allowDeliverables && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      p: 1.25,
                      mx: 1.25,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Inventory2OutlinedIcon
                          sx={{ fontSize: 18, color: "#6B7280" }}
                        />
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#0D0D0E",
                            fontFamily: "Inter Tight",
                          }}
                        >
                          Deliverables
                        </Typography>
                        <Box
                          sx={{
                            padding: "1px 8px",
                            borderRadius: "999px",
                            backgroundColor: "#F3F4F6",
                            color: "#6B7280",
                            fontSize: 12,
                            fontWeight: 500,
                            fontFamily: "Inter Tight",
                          }}
                        >
                          {deliverables.length}
                        </Box>
                      </Box>
                      {!isDisabled && (
                        <button
                          type="button"
                          onClick={() =>
                            handleAddDeliverable(index, deliverables.length)
                          }
                          style={{
                            backgroundColor: "transparent",
                            border: "1px dashed #4D6BF6",
                            color: "#4D6BF6",
                            borderRadius: "999px",
                            padding: "6px 14px",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: "Inter Tight",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <AddIcon sx={{ fontSize: 14 }} />
                          Add deliverable
                        </button>
                      )}
                    </Box>

                    {deliverables.length === 0 ? (
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "#9CA3AF",
                          fontFamily: "Inter Tight",
                          py: 1.5,
                          textAlign: "center",
                        }}
                      >
                        No deliverables yet. Click "Add deliverable" to create
                        one.
                      </Typography>
                    ) : (
                      deliverables.map((d, di) => (
                        <DeliverableRow
                          key={d.id || di}
                          deliverable={d}
                          index={di}
                          onUpdateName={(text) =>
                            updateDeliverable?.(index, di, {
                              deliverable_description: text,
                            })
                          }
                          onDelete={() => deleteDeliverable?.(index, di)}
                          onAddSub={() => addSubActivity?.(index, di)}
                          onUpdateSubName={(sIdx, text) =>
                            updateSubActivity?.(index, di, sIdx, {
                              sub_activity_description: text,
                            })
                          }
                          onDeleteSub={(sIdx) =>
                            deleteSubActivity?.(index, di, sIdx)
                          }
                          disabled={isDisabled}
                          autoFocus={
                            autoFocusKey.ms === index && autoFocusKey.del === di
                          }
                        />
                      ))
                    )}
                  </Box>
                </Collapse>
              )}
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </CardContent>
    </Card>
  );
};

export default BillingMilestones;
